import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ORBI_PLATFORM_HEADER,
  ORBI_PLATFORM_WEB,
} from '@patient-ui/shared/constants';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/**
 * HTTP interceptor that stamps the platform identifier header on EVERY request
 * to the Orbi AI agent service (PCSA-13225).
 *
 * The agent service is shared across platforms; the header lets it tell a
 * Patient Portal web request apart from the mobile app and respond accordingly.
 * An interceptor (rather than setting the header per call in `OrbiService`)
 * guarantees the story's "consistently, regardless of request type" requirement:
 * messages, history (fetch/rename/delete), feedback, and any future agent call
 * are covered automatically, with no call site able to forget it.
 *
 * Matching mirrors the existing `TimezoneInterceptor` idiom — the header is
 * added only to requests whose URL targets `orbiApiBaseUrl`, so non-agent
 * traffic is untouched. When Orbi is not configured for the environment
 * (`orbiApiBaseUrl` empty) no request can match, and everything passes through
 * unchanged.
 */
@Injectable()
export class OrbiPlatformInterceptor implements HttpInterceptor {
  constructor(private readonly environmentService: EnvironmentService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const orbiApiBaseUrl = this.environmentService.orbiApiBaseUrl;

    // Only agent-service requests carry the platform header; everything else
    // (and every request when the feature is unconfigured) passes through.
    if (!orbiApiBaseUrl || !req.url.startsWith(orbiApiBaseUrl)) {
      return next.handle(req);
    }

    const clonedRequest = req.clone({
      setHeaders: {
        [ORBI_PLATFORM_HEADER]: ORBI_PLATFORM_WEB,
      },
    });
    return next.handle(clonedRequest);
  }
}
